import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { cleanText } from '../lib/pi.js';
import { TZ, thParts } from '../lib/thTime.js';   // เวลาไทยเสมอ ไม่ตามนาฬิกาเครื่อง

const MONTHS_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const MOVE_TH = {
  receive: 'รับเข้า', sale: 'ขาย', withdraw: 'ตัดเบิก',
  transfer_in: 'โอนเข้า', transfer_out: 'โอนออก', move: 'ย้าย bin', adjust: 'ปรับยอด',
};
const fmt = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const baht = n => n >= 1e6 ? `฿${(n / 1e6).toFixed(n >= 1e7 ? 1 : 2)}M` : n >= 1e3 ? `฿${Math.round(n / 1e3)}K` : `฿${fmt(n)}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('th-TH-u-ca-gregory', { timeZone: TZ, day: 'numeric', month: 'short', year: '2-digit' }) : '—';
// ปี + เดือน(0-11) ตามเวลาไทย — ใช้แบ่งปี/เดือน/ไตรมาส (เดิม getFullYear/getMonth ตามนาฬิกาเครื่อง)
const thYM = v => { const p = thParts(v); return { y: p.y, mo: p.m - 1 }; };

// เฉดสีแท่งซ้อน: แบรนด์อันดับ 1 เข้มสุด ไล่ลงไป · "อื่นๆ" อ่อนสุด
const STACK_INK = ['#0A0A0A', '#3E3E3E', '#676767', '#8F8F8F', '#B5B5B5', '#D8D8D8', '#ECECEC'];

// line value: SourcePoint POs carry provisional THB cost; legacy preorder POs
// only have unit_price — use what exists so old data still shows.
const lineVal = i => Number(i.quantity || 0) * Number(i.provisional_unit_cost ?? i.unit_price ?? 0);

// "Brand" in purchasing = the supplier's brand on the PO (company ≠ brand, set in
// the Suppliers page). Fallbacks: supplier company → catalog brand — the catalog's
// brands table holds Zort category strings, so it comes last.
const brandOf = i =>
  i.po?.supplier?.default_brand?.name
  ?? i.po?.supplier?.company_name
  ?? i.product?.brand?.name
  ?? '(ไม่ระบุแบรนด์)';

// ชื่อแบรนด์สองระบบสะกดไม่เท่ากัน ("Snow Peak Inc." / "TOKYO CRAFTS" ฝั่ง BUY vs
// "Snow Peak" / "TOKYOCRAFTS" ฝั่ง Zort) — โหมดรวมต้องเป็นชื่อเดียวกัน เทียบแบบตัดช่องว่าง/จุด
const CANON = [
  ['snowpeak', 'Snow Peak'], ['nanga', 'NANGA'], ['klattermusen', 'Klattermusen'],
  ['dod', 'DoD'], ['shaka', 'SHAKA'], ['zpacks', 'Zpacks'], ['barebones', 'Barebones'],
  ['chums', 'CHUMS'], ['5050workshop', '5050WORKSHOP'], ['evernew', 'EVERNEW'],
  ['oregonian', 'Oregonian Camper'], ['helinox', 'Helinox'], ['kermit', 'KERMIT CHAIR'],
  ['tokyocrafts', 'TOKYOCRAFTS'],
];
const canonBrand = b => {
  if (!b) return 'อื่นๆ';
  const k = b.toLowerCase().replace(/[^a-z0-9฀-๿]/g, '');
  for (const [pre, c] of CANON) if (k.startsWith(pre)) return c;
  return b;
};

export default function Stats() {
  const [items, setItems] = useState(null);   // purchase_order_items + product + po (ข้อมูลปัจจุบัน BUY)
  const [zortDaily, setZortDaily] = useState(null); // ข้อมูลเก่า Zort: (doc, วัน, แบรนด์) — โหลดเมื่อสลับครั้งแรก
  const [zortBought, setZortBought] = useState([]); // รายสินค้าในแบรนด์ที่เจาะดู (ฝั่ง Zort)
  const [dataset, setDataset] = useState('buy');    // 'buy' | 'zort' | 'all' — ตัดกันที่ 1 ส.ค. 2026
  const [gran, setGran] = useState('month');        // 'month' | 'quarter' | 'year'
  const [sellThrough, setSellThrough] = useState([]);
  const [sellNames, setSellNames] = useState(new Map());
  const [year, setYear] = useState(null);
  const [brand, setBrand] = useState('all');
  const [q, setQ] = useState('');
  const [suggest, setSuggest] = useState([]);
  const [detail, setDetail] = useState(null); // { product, monthly, ledger, st }

  // browser back unwinds one drill level: product detail → (brand view) → all brands.
  // Each drill-in pushes a history entry; popstate pops one UI level.
  useEffect(() => {
    const onPop = () => {
      setDetail(d => {
        if (d) return null;                       // close product detail, keep filters
        setBrand(b => (b !== 'all' ? 'all' : b)); // leave brand drill
        return d;
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const drillBrand = b => {
    window.history.pushState(null, '', '#stats');
    setBrand(b);
  };

  useEffect(() => {
    (async () => {
      const [{ data: poi }, { data: st }] = await Promise.all([
        supabase.from('purchase_order_items').select(`
          quantity, unit_price, provisional_unit_cost,
          product:products(id, sku, name, image_url, brand:brands(id, name)),
          po:purchase_orders(id, created_at, status, archived,
            supplier:suppliers(company_name, default_brand:brands!suppliers_default_brand_id_fkey(name)))
        `).limit(3000),
        supabase.from('v_sell_through').select('*').order('total_out', { ascending: false }).limit(60),
      ]);
      const clean = (poi ?? []).filter(i => i.po && i.po.status !== 'cancelled' && !i.po.archived && i.product);
      setItems(clean);
      setSellThrough(st ?? []);
      const years = [...new Set(clean.map(i => thParts(i.po.created_at).y))].sort((a, b) => b - a);
      setYear(y => y ?? years[0] ?? thParts(new Date()).y);
      const ids = (st ?? []).map(r => r.product_id);
      if (ids.length) {
        const { data: prods } = await supabase.from('products')
          .select('id, sku, name, image_url, brand:brands(id, name)').in('id', ids);
        setSellNames(new Map((prods ?? []).map(p => [p.id, p])));
      }
    })();
  }, []);

  // ข้อมูลเก่า Zort — โหลดครั้งเดียวตอนสลับมาดู (public.zort_pb_daily, เฉพาะใบ Success)
  useEffect(() => {
    if (dataset === 'buy' || zortDaily) return;
    (async () => {
      const { data } = await supabase.from('zort_pb_daily').select('*').limit(2000);
      setZortDaily(data ?? []);
    })();
  }, [dataset, zortDaily]);

  // product search suggestions
  useEffect(() => {
    const t = cleanText(q).trim();
    if (t.length < 2) { setSuggest([]); return; }
    const h = setTimeout(async () => {
      const { data } = await supabase.from('products')
        .select('id, sku, name, image_url, brand:brands(id, name)')
        .or(`name.ilike.%${t}%,sku.ilike.%${t}%`).limit(8);
      setSuggest(data ?? []);
    }, 250);
    return () => clearTimeout(h);
  }, [q]);

  // ---- unified records: {d, brand, val, qty, docId, item?} ทุกชุดข้อมูลหน้าตาเดียวกัน ----
  // docId ไม่ชนกันข้ามระบบ: BUY = uuid, Zort = เลข id — โหมดรวม concat ได้ตรง ๆ
  const buyRecords = useMemo(() => (items ?? []).map(i => ({
    d: new Date(i.po.created_at), ...thYM(i.po.created_at), brand: canonBrand(brandOf(i)),
    val: lineVal(i), qty: Number(i.quantity || 0), docId: i.po.id, item: i,
  })), [items]);
  const zortRecords = useMemo(() => (zortDaily ?? []).map(r => ({
    d: new Date(r.po_date), ...thYM(r.po_date), brand: canonBrand(r.brand),
    val: Number(r.val), qty: Number(r.qty), docId: r.doc_id,
  })), [zortDaily]);
  const records = useMemo(() =>
    dataset === 'buy' ? buyRecords
    : dataset === 'zort' ? zortRecords
    : [...buyRecords, ...zortRecords], [dataset, buyRecords, zortRecords]);

  const years = useMemo(
    () => [...new Set(records.map(r => r.y))].sort((a, b) => b - a),
    [records]);

  // สลับชุดข้อมูลแล้วปีที่เลือกอาจไม่มีในชุดใหม่ → เด้งไปปีล่าสุดของชุดนั้น
  useEffect(() => {
    if (years.length && !years.includes(year)) setYear(years[0]);
  }, [years, year]);

  function switchDataset(ds) {
    if (ds === dataset) return;
    setDataset(ds);
    setBrand('all');
  }

  const brands = useMemo(() => [...new Set(records.map(r => r.brand))].sort(), [records]);
  // product → brand label, learned from what we actually purchased
  const prodBrand = useMemo(() => {
    const m = new Map();
    for (const i of items ?? []) if (!m.has(i.product.id)) m.set(i.product.id, canonBrand(brandOf(i)));
    return m;
  }, [items]);

  // โหมด "ปี" ดูภาพรวมทุกปี · เดือน/Quarter ดูเฉพาะปีที่เลือก
  const timeScoped = useMemo(() => records.filter(r =>
    gran === 'year' || r.y === year), [records, gran, year]);
  const scoped = useMemo(() => timeScoped.filter(r =>
    brand === 'all' || r.brand === brand), [timeScoped, brand]);

  // โหมดรวม: ผสานรายสินค้าในแบรนด์จากทั้งสองระบบด้วย SKU (BUY มี product ผูก = คลิกดูต่อได้)
  // ⚠️ ต้องประกาศก่อน skuCount ด้านล่าง (const ใน render — อ้างก่อนประกาศ = จอขาว TDZ)
  const allBought = useMemo(() => {
    if (dataset !== 'all' || brand === 'all') return [];
    const map = new Map();
    for (const r of scoped) {
      if (!r.item) continue;
      const p = r.item.product;
      const k = (p.sku || p.name || '').toUpperCase();
      const cur = map.get(k) ?? { sku: p.sku, name: p.name, qty: 0, val: 0, product: p };
      cur.qty += r.qty; cur.val += r.val;
      map.set(k, cur);
    }
    for (const z of zortBought) {
      const k = (z.sku || z.name || '').toUpperCase();
      const cur = map.get(k) ?? { sku: z.sku, name: z.name, qty: 0, val: 0, product: null };
      cur.qty += z.qty; cur.val += z.val;
      map.set(k, cur);
    }
    return [...map.values()].sort((a, b) => b.val - a.val).slice(0, 40);
  }, [dataset, brand, scoped, zortBought]);

  // ---- แท่งซ้อนแยกแบรนด์ ตามช่วงเวลาที่เลือก ----
  const stack = useMemo(() => {
    let buckets;
    if (gran === 'month') buckets = MONTHS_TH.map((m, i) => ({ key: i, label: m }));
    else if (gran === 'quarter') buckets = ['Q1', 'Q2', 'Q3', 'Q4'].map((m, i) => ({ key: i, label: m }));
    else buckets = [...years].sort((a, b) => a - b).map(y => ({ key: y, label: String(y) }));
    const bucketOf = r => gran === 'month' ? r.mo
      : gran === 'quarter' ? Math.floor(r.mo / 3) : r.y;

    const bTot = new Map();
    for (const r of scoped) bTot.set(r.brand, (bTot.get(r.brand) ?? 0) + r.val);
    const top = [...bTot.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([b]) => b);
    const isTop = new Set(top);
    const series = bTot.size > top.length ? [...top, 'อื่นๆ*'] : top;

    const data = buckets.map(bk => ({ ...bk, total: 0, segs: Object.fromEntries(series.map(s => [s, 0])) }));
    const idx = new Map(buckets.map((bk, i) => [bk.key, i]));
    for (const r of scoped) {
      const i = idx.get(bucketOf(r));
      if (i == null) continue;
      const s = isTop.has(r.brand) ? r.brand : 'อื่นๆ*';
      data[i].segs[s] += r.val;
      data[i].total += r.val;
    }
    return { series, data, max: Math.max(...data.map(d => d.total), 1) };
  }, [scoped, gran, years]);

  const totalVal = scoped.reduce((t, r) => t + r.val, 0);
  const poCount = new Set(scoped.map(r => r.docId)).size;
  const unitsIn = scoped.reduce((t, r) => t + r.qty, 0);
  const skuCount = dataset === 'zort' ? zortBought.length
    : dataset === 'all' ? allBought.length
    : new Set(scoped.map(r => r.item?.product.id).filter(Boolean)).size;

  const brandRank = useMemo(() => {
    const map = new Map();
    for (const r of timeScoped) {
      const cur = map.get(r.brand) ?? { val: 0, qty: 0 };
      cur.val += r.val; cur.qty += r.qty;
      map.set(r.brand, cur);
    }
    return [...map.entries()].map(([b, v]) => ({ b, ...v })).sort((a, x) => x.val - a.val);
  }, [timeScoped]);
  const rankMax = brandRank[0]?.val || 1;
  const rankTotal = brandRank.reduce((t, r) => t + r.val, 0) || 1;

  const boughtInBrand = useMemo(() => {
    if (brand === 'all' || dataset !== 'buy') return [];
    const map = new Map();
    for (const r of scoped) {
      if (!r.item) continue;
      const cur = map.get(r.item.product.id) ?? { product: r.item.product, qty: 0 };
      cur.qty += r.qty;
      map.set(r.item.product.id, cur);
    }
    return [...map.values()].sort((a, b) => b.qty - a.qty);
  }, [scoped, brand, dataset]);

  // ฝั่ง Zort: เจาะแบรนด์แล้วดึงรายสินค้าจาก view (aggregate ต่อ sku ต่อปีใน DB) — ใช้ทั้งโหมด zort และรวม
  useEffect(() => {
    if (dataset === 'buy' || brand === 'all') { setZortBought([]); return; }
    (async () => {
      let query = supabase.from('zort_pb_products').select('*').eq('brand', brand)
        .order('val', { ascending: false }).limit(120);
      if (gran !== 'year') query = query.eq('year', year);
      const { data } = await query;
      const map = new Map();
      for (const r of data ?? []) {
        const k = r.sku || r.name;
        const cur = map.get(k) ?? { sku: r.sku, name: r.name, qty: 0, val: 0 };
        cur.qty += Number(r.qty); cur.val += Number(r.val);
        map.set(k, cur);
      }
      setZortBought([...map.values()].sort((a, b) => b.val - a.val).slice(0, 40));
    })();
  }, [dataset, brand, year, gran]);

  const sells = useMemo(() => sellThrough
    .map(r => ({
      ...r,
      product: sellNames.get(r.product_id),
      brandLabel: prodBrand.get(r.product_id) ?? sellNames.get(r.product_id)?.brand?.name ?? '',
    }))
    .filter(r => r.product && (brand === 'all' || r.brandLabel === brand))
    .slice(0, 8), [sellThrough, sellNames, brand, prodBrand]);

  async function openProduct(p) {
    setQ(''); setSuggest([]);
    window.history.pushState(null, '', '#stats');
    const [{ data: mm }, { data: mv }] = await Promise.all([
      supabase.from('v_product_movement_monthly').select('*').eq('product_id', p.id).order('month'),
      supabase.schema('wh').from('movements').select('occurred, type, qty, by_who, dest, location')
        .eq('sku', p.sku).order('occurred', { ascending: true }).limit(200),
    ]);
    let bal = 0;
    const ledger = (mv ?? []).map(m => { bal += Number(m.qty); return { ...m, bal }; }).reverse().slice(0, 40);
    const st = sellThrough.find(r => r.product_id === p.id);
    setDetail({ product: p, monthly: mm ?? [], ledger, st });
  }

  if (!items) return <div className="todo-page">กำลังโหลดสถิติ…</div>;
  const zortLoading = dataset !== 'buy' && zortDaily === null;

  /* ---------- product drill-down ---------- */
  if (detail) {
    const { product: p, monthly: mm, ledger, st } = detail;
    const totalIn = mm.reduce((t, r) => t + Number(r.qty_in), 0);
    const totalOut = mm.reduce((t, r) => t + Number(r.qty_out), 0);
    const last = mm.slice(-8);
    const gmax = Math.max(...last.flatMap(r => [Number(r.qty_in), Number(r.qty_out)]), 1);
    return (
      <section>
        <div className="back-row"><a onClick={() => window.history.back()}>← กลับหน้าก่อนหน้า</a></div>
        <div className="pd-head">
          {p.image_url
            ? <div className="thumb lg"><img src={p.image_url} alt="" /></div>
            : <div className="thumb lg empty" />}
          <div className="pd-h-txt">
            <h1 style={{ fontSize: 40 }}>{p.name}</h1>
            <div className="meta">
              {(prodBrand.get(p.id) ?? p.brand?.name) && <span className="tag solid">{prodBrand.get(p.id) ?? p.brand?.name}</span>}
              <span className="mono">{p.sku ?? '—'}</span>
            </div>
          </div>
        </div>
        <div className="kpi-row">
          <div className="kpi"><div className="v">{fmt(totalIn)}</div><div className="l">ซื้อเข้ารวม<span className="en">Units in</span></div></div>
          <div className="kpi"><div className="v">{fmt(totalOut)}</div><div className="l">ขายออกรวม<span className="en">Units out</span></div></div>
          <div className="kpi"><div className="v">{fmt(totalIn - totalOut)}</div><div className="l">คงเหลือ (ตาม ledger)<span className="en">Balance</span></div></div>
          <div className="kpi"><div className="v">{st ? `${st.sell_through_pct}%` : '—'}</div><div className="l">sell-through<span className="en">Sold ÷ purchased</span></div></div>
        </div>

        <div className="stats-sec">
          <div className="stats-sec-head">
            <h3>เข้า–ออก รายเดือน</h3>
            <div className="legend"><span><i className="ink" />เข้า</span><span><i className="amb" />ออก</span></div>
          </div>
          {last.length ? (
            <>
              <div className="gchart">
                {last.map(r => (
                  <div className="gcol" key={r.month}>
                    <div className="gbars">
                      <div className="gbar inb" style={{ height: `${Math.round(Number(r.qty_in) / gmax * 100)}%` }} />
                      <div className="gbar outb" style={{ height: `${Math.round(Number(r.qty_out) / gmax * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="chart-x">
                {last.map(r => <span key={r.month}>{MONTHS_TH[thParts(r.month).m - 1]}</span>)}
              </div>
            </>
          ) : <p className="sd-empty">ยังไม่มีความเคลื่อนไหว</p>}
        </div>

        <div className="stats-sec" style={{ paddingBottom: 90 }}>
          <div className="stats-sec-head"><h3>ความเคลื่อนไหวล่าสุด</h3><span className="src">จาก ledger คลัง (wh.movements)</span></div>
          <div className="led-head"><span>วันที่</span><span>รายการ</span><span className="r">จำนวน</span><span className="r">คงเหลือ</span></div>
          {ledger.length ? ledger.map((m, i) => (
            <div className="led-row" key={i}>
              <span className="led-date">{fmtDate(m.occurred)}</span>
              <span className="led-type">{MOVE_TH[m.type] ?? m.type}<span className="rf">{m.dest || m.location || m.by_who || ''}</span></span>
              <span className={`led-qty ${Number(m.qty) >= 0 ? 'pos' : 'neg'}`}>{Number(m.qty) > 0 ? '+' : ''}{fmt(m.qty)}</span>
              <span className="led-bal">{fmt(m.bal)}</span>
            </div>
          )) : <p className="sd-empty">ยังไม่มีรายการใน ledger</p>}
        </div>
      </section>
    );
  }

  /* ---------- main stats ---------- */
  const granLabel = gran === 'month' ? 'รายเดือน' : gran === 'quarter' ? 'รายไตรมาส' : 'รายปี';
  const scopeLabel = `${brand === 'all' ? 'ทุกแบรนด์' : brand} · ${gran === 'year' ? 'ทุกปี' : `ปี ${year}`}`;
  return (
    <section>
      <div className="hero">
        <div className="eyebrow">สถิติ · Analytics</div>
        <h1>ซื้ออะไร<br />แบรนด์ไหน แค่ไหน</h1>
        <p className="sub">
          {dataset === 'zort'
            ? 'ข้อมูลเก่าจากระบบ Zort (ถึง 31 ก.ค. 2026) · เฉพาะใบสั่งซื้อที่สำเร็จ · จับแบรนด์จาก PD + AI'
            : dataset === 'all'
            ? 'ภาพรวมทุกระบบ — App BUY (ปัจจุบัน) + ข้อมูลเก่า Zort (ถึง 31 ก.ค. 2026 เฉพาะใบสำเร็จ) รวมในภาพเดียว'
            : 'มูลค่าการซื้อแยกตามแบรนด์ ดูเป็นรายเดือน ไตรมาส หรือรายปี · ซื้ออะไรเข้ามาบ้าง และของอะไรขายดี (sell-through อ่านจาก POS)'}
        </p>
      </div>

      {dataset === 'buy' && (
      <div className="prod-search">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="#0A0A0A" strokeWidth="1.6"/><path d="M20 20l-3.5-3.5" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
        <input value={q} onChange={e => setQ(e.target.value)} autoComplete="off"
               placeholder="ค้นหาสินค้าเพื่อดูการเคลื่อนไหว ซื้อเข้า–ขายออก…" />
        {suggest.length > 0 && (
          <div className="suggest open">
            {suggest.map(p => (
              <div className="suggest-row" key={p.id} onClick={() => openProduct(p)}>
                <span className="nm">{p.name}</span>
                <span className="br">{p.brand?.name ?? ''} · {p.sku ?? ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      <div className="stats-controls">
        <div className="seg">
          <button className={dataset === 'all' ? 'on' : ''} onClick={() => switchDataset('all')}>ข้อมูลทั้งหมด</button>
          <button className={dataset === 'buy' ? 'on' : ''} onClick={() => switchDataset('buy')}>App BUY</button>
          <button className={dataset === 'zort' ? 'on' : ''} onClick={() => switchDataset('zort')}>ข้อมูลเก่า ZORT</button>
        </div>
        <div className="seg">
          <button className={gran === 'month' ? 'on' : ''} onClick={() => setGran('month')}>เดือน</button>
          <button className={gran === 'quarter' ? 'on' : ''} onClick={() => setGran('quarter')}>Quarter</button>
          <button className={gran === 'year' ? 'on' : ''} onClick={() => setGran('year')}>ปี</button>
        </div>
        {gran !== 'year' && (
        <div className="seg">
          {years.map(y => (
            <button key={y} className={y === year ? 'on' : ''} onClick={() => setYear(y)}>ปี {y}</button>
          ))}
        </div>
        )}
        <div className="brand-filter">
          <label>แบรนด์</label>
          <select value={brand} onChange={e => setBrand(e.target.value)}>
            <option value="all">ทุกแบรนด์</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>

      {zortLoading ? <div className="todo-page">กำลังโหลดข้อมูลเก่าจาก Zort…</div> : (
      <>
      <div className="kpi-row">
        <div className="kpi"><div className="v">{baht(totalVal)}</div><div className="l">มูลค่าซื้อรวม<span className="en">Purchase value</span></div></div>
        <div className="kpi"><div className="v">{poCount}</div><div className="l">ใบสั่งซื้อ<span className="en">Purchase orders</span></div></div>
        <div className="kpi"><div className="v">{fmt(unitsIn)}</div><div className="l">จำนวนชิ้น<span className="en">Units in</span></div></div>
        <div className="kpi"><div className="v">{brand === 'all' ? brandRank.length : skuCount}</div><div className="l">{brand === 'all' ? 'แบรนด์' : 'SKU ที่ซื้อ'}<span className="en">{brand === 'all' ? 'Brands' : 'SKUs'}</span></div></div>
      </div>

      <div className="stats-sec">
        <div className="stats-sec-head">
          <h3>มูลค่าการซื้อ{granLabel} — แยกแบรนด์</h3>
          <span className="src">{scopeLabel} · รวม {baht(totalVal)}</span>
        </div>
        <div className="chart">
          {stack.data.map((c, i) => (
            <div className="bar-col" key={i}>
              <span className="bar-v">{c.total > 0 ? baht(c.total) : ''}</span>
              <div className="bar-stack" style={{ height: `${Math.round(c.total / stack.max * 100)}%` }}>
                {stack.series.map((s, si) => c.segs[s] > 0 ? (
                  <div key={s} className="bar-seg" title={`${s} · ${baht(c.segs[s])}`}
                       style={{ height: `${c.segs[s] / c.total * 100}%`, background: STACK_INK[Math.min(si, STACK_INK.length - 1)] }} />
                ) : null)}
              </div>
            </div>
          ))}
        </div>
        <div className="chart-x">{stack.data.map(c => <span key={c.label}>{c.label}</span>)}</div>
        {stack.series.length > 1 && (
          <div className="stack-legend">
            {stack.series.map((s, si) => (
              <span key={s}><i style={{ background: STACK_INK[Math.min(si, STACK_INK.length - 1)] }} />{s}</span>
            ))}
          </div>
        )}
      </div>

      <div className={`stats-grid ${dataset !== 'buy' ? 'single' : ''}`}>
        <div>
          <div className="stats-sec-head">
            <h3>{brand === 'all' ? 'แบรนด์ — ซื้อเท่าไหร่' : `ซื้ออะไรมาบ้าง — ${brand}`}</h3>
            {brand !== 'all' && <span className="back" onClick={() => window.history.back()}>← ทุกแบรนด์</span>}
          </div>
          {brand === 'all' ? (
            brandRank.length ? brandRank.map((r, i) => (
              <div className={`rank-row ${i === 0 ? 'top' : ''}`} key={r.b} onClick={() => drillBrand(r.b)}>
                <div className="rank-n">{String(i + 1).padStart(2, '0')}</div>
                <div className="rank-main">
                  <div className="nm">{r.b}</div>
                  <div className="sub">{Math.round(r.val / rankTotal * 100)}% ของยอดซื้อ</div>
                  <div className="share"><i style={{ width: `${Math.round(r.val / rankMax * 100)}%` }} /></div>
                </div>
                <div className="rank-val">{baht(r.val)}<span className="q">{fmt(r.qty)} ชิ้น</span></div>
              </div>
            )) : <p className="sd-empty">ยังไม่มีข้อมูลช่วงนี้</p>
          ) : dataset === 'buy' ? (
            boughtInBrand.length ? boughtInBrand.map((r, i) => (
              <div className="rank-row" key={r.product.id} onClick={() => openProduct(r.product)}>
                <div className="rank-n">{String(i + 1).padStart(2, '0')}</div>
                <div className="rank-main"><div className="nm">{r.product.name}</div></div>
                <div className="rank-val">{fmt(r.qty)}<span className="q">ชิ้น</span></div>
              </div>
            )) : <p className="sd-empty">ยังไม่มีการซื้อช่วงนี้</p>
          ) : (
            (dataset === 'all' ? allBought : zortBought).length
              ? (dataset === 'all' ? allBought : zortBought).map((r, i) => (
              <div className={`rank-row ${r.product ? '' : 'static'}`} key={r.sku || r.name}
                   onClick={r.product ? () => openProduct(r.product) : undefined}>
                <div className="rank-n">{String(i + 1).padStart(2, '0')}</div>
                <div className="rank-main"><div className="nm">{r.name || r.sku}</div>{r.sku && <div className="sub mono">{r.sku}</div>}</div>
                <div className="rank-val">{baht(r.val)}<span className="q">{fmt(r.qty)} ชิ้น</span></div>
              </div>
            )) : <p className="sd-empty">ยังไม่มีการซื้อช่วงนี้</p>
          )}
        </div>
        {dataset === 'buy' && (
        <div>
          <div className="stats-sec-head"><h3>ขายดี — sell-through</h3><span className="src">จาก POS</span></div>
          {sells.length ? sells.map(s => (
            <div className="sell-row" key={s.product_id} onClick={() => openProduct(s.product)}>
              <div className="sell-main"><div className="nm">{s.product.name}</div><div className="br">{s.brandLabel}</div></div>
              <div className="sell-r"><div className="u">{fmt(s.total_out)}</div><div className="pct">ขายออก {s.sell_through_pct}%</div></div>
            </div>
          )) : <p className="sd-empty">ยังไม่มีข้อมูลขาย</p>}
        </div>
        )}
      </div>
      </>
      )}
    </section>
  );
}
